import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { linkpassPost } from '../../../utils/fetch';
import numberWithCommas from '../../../utils/numberWithCommas';
import Button from '../../Button';
import { Box, Col, Row } from '../../Grid';
import convertPartnerPoints from '../convertPartnerPoints';

import FormDialog from './FormDialog';

const LINK_FAILED_TEXT =
  'Sorry, we could not start the linking process just now. Please try again later.';

/*
 * The linkage POST needs the member's id on the partner side, and config.formFields is the
 * only thing that asks for it. A partner that sends none cannot be linked from this page.
 */
const LINK_UNAVAILABLE_TEXT =
  'Sorry, linking this account is not available here yet. Please try again later.';

const ExchangeConnect = ({ partnerData, user }) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();

  const { id, availableLpBalance, transactionDurationNote, permalink, config } = partnerData;
  const { linkPartnerText, merchantExchangeUrl, merchantExchangeText, formFields = [] } = config;

  const [loginParams, setLoginParams] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [errorText, setErrorText] = useState('');

  const clearError = () => {
    setErrorText('');
  };

  /*
   * Linkpass: POST /pex/me/exchange-linkages replaces getLoginUrl + save. It takes the
   * exchange's Salesforce id and the member's partner-side id, so neither the numeric
   * exchangeId nor partnerIdentity the legacy pair needed is involved any more.
   */
  async function linkPartnerAccount(externalId, fieldset) {
    try {
      await linkpassPost('/pex/me/exchange-linkages', { exchangeId: id, externalId });
      window.location.reload();
    } catch (err) {
      // linkpassFetch keeps the API's own member-facing copy on the error it throws
      setErrorText(err.serverMessage || LINK_FAILED_TEXT);
      if (fieldset) {
        fieldset.disabled = false;
      }
    }
  }

  /*
   * config.formFields already describes the dialog, so there is nothing to fetch before
   * opening it. With no fields there is no way to ask for the partner-side id the POST
   * requires, so say so rather than sending an empty one.
   */
  function handlePartnerLoginClick() {
    if (!formFields.length) {
      setErrorText(LINK_UNAVAILABLE_TEXT);
      return;
    }
    setLoginParams({ formFields });
    setModalVisible(true);
  }

  async function handleFormSubmit(values, e) {
    const fieldset = e.target.querySelector('fieldset');
    fieldset.disabled = true;

    // one field, as the legacy save assumed too - its value is the partner-side id
    const [externalId] = Object.values(values);
    await linkPartnerAccount(externalId, fieldset);
  }

  const redirectUrl = encodeURIComponent(`/exchange/${permalink}`);

  /*
   * Some partners cannot be linked from this page at all - the exchange happens in their own
   * app, and all we show is a button through to it. Legacy identified them by
   * partnerIdentity === 1, which Linkpass does not issue, so the same partners have to be
   * recognised from what the API does describe: no form fields to ask for the member's
   * partner-side id, and no label for a link button. Atome+ is the case in point - showing
   * it "Link your Atome+ Account" offers something that cannot work.
   */
  const redirectOnly = Boolean(merchantExchangeUrl) && !formFields.length && !linkPartnerText;

  return (
    <>
      {redirectOnly && (
        <Button
          href={`${merchantExchangeUrl}`}
          target="_blank"
          rel="noreferrer noopener"
        >{`${merchantExchangeText}`}</Button>
      )}
      {!redirectOnly && (
        <div>
          {!user.loggedInPlus ? (
            <div>
              {transactionDurationNote && (
                <Box my="lg">
                  <small>
                    Please note that it will take up to {transactionDurationNote} to process your
                    conversion request.
                  </small>
                </Box>
              )}
              <Button href={`/login?redirect=${redirectUrl}`}>Log in to your Link account</Button>
            </div>
          ) : (
            <Row flexDirection={['column', 'row']} mt="lg">
              <Col flex="1" flexBasis={['0', '50%']}>
                Linkpoints Balance:{' '}
                {availableLpBalance && <strong>{numberWithCommas(availableLpBalance)}</strong>}
                <div className="exchange-member-id">Member ID: {user.accesstoken}</div>
              </Col>
              <Col flex="1" flexBasis={['0', '50%']}>
                <Button block onClick={handlePartnerLoginClick} data-testid="link-partner-button">
                  {linkPartnerText || `Link your ${partnerData.merchantName} Account`}
                </Button>
                {merchantExchangeUrl && (
                  <Button mt="md" onClick={convertPartnerPoints(merchantExchangeUrl)}>
                    {merchantExchangeText || 'Go to partner website to exchange points'}
                  </Button>
                )}
              </Col>
            </Row>
          )}
        </div>
      )}
      {/* FormDialog shows errorText too, but only once it is open - a failure before that
          leaves the member with no feedback at all, so it is also shown here. */}
      {errorText && !modalVisible && (
        <Box mt="md" role="alert">
          <small>{errorText}</small>
        </Box>
      )}
      <FormDialog
        errorText={errorText}
        errors={errors}
        register={register}
        loginParams={loginParams}
        modalVisible={modalVisible}
        onSubmit={handleSubmit(handleFormSubmit)}
        onDismiss={() => {
          setModalVisible(false);
          clearError();
        }}
        clearError={clearError}
      />
    </>
  );
};

export default ExchangeConnect;
